import React, { useState } from 'react';
import { Clock, Loader2, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from './common/Button';
import GradientLogo from './common/GradientLogo';
import Modal from './common/Modal';
import PrivacyPolicyContent from './PrivacyPolicyContent';
import { supabase, withRetry } from '../lib/supabase';
import { useUtmParams } from '../hooks/useUtmParams';
import { encryptData } from '../utils/crypto';

const LANCAMENTO = 1;

const Hero = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState(false);
  const utmParams = useUtmParams();

  // Event date information
  const eventDate = {
    date: '09 de Junho, 2025',
    time: '20:00',
    timezone: 'BRT'
  };

  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const emailTrimmed = formData.email.toLowerCase().trim();
      const nameTrimmed = formData.name.trim();

      // Gera o hash para a pesquisa
      const pesquisaHash = encryptData({ email: emailTrimmed, lancamento: LANCAMENTO });

      // Verifica se já existe um lead com este email neste lançamento
      const { data: existingLead, error: searchError } = await supabase
        .from('leads_lancamento')
        .select('id')
        .eq('email', emailTrimmed)
        .eq('lancamento', LANCAMENTO)
        .single();

      if (searchError && searchError.code !== 'PGRST116') {
        throw searchError;
      }

      if (existingLead?.id) {
        // Se já existe, atualiza o hash e redireciona
        const { error: updateError } = await supabase
          .from('leads_lancamento')
          .update({ pesquisa_hash: pesquisaHash })
          .eq('id', existingLead.id);

        if (updateError) throw updateError;

        const encryptedData = encryptData({ email: emailTrimmed, lancamento: LANCAMENTO });
        navigate(`/inscricao/obrigado?data=${encryptedData}`);
        return;
      }

      let clientIp = '';
      
      try {
        const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
          mode: 'cors',
          credentials: 'omit'
        });
        if (response.ok) {
          const data = await response.text();
          const params = new Map(
            data.trim().split('\n').map(line => {
              const [key, value] = line.split('=');
              return [key, value];
            })
          );
          
          clientIp = params.get('ip') || '';
        }
      } catch (error) {
        console.warn('Error fetching IP:', error);
        // Continue with empty IP if fetch fails
      }

      const leadData = {
        name: nameTrimmed,
        email: emailTrimmed,
        ...utmParams,
        consent_given: true,
        consent_timestamp: new Date().toISOString(),
        consent_ip: clientIp,
        consent_user_agent: navigator.userAgent,
        consent_language: navigator.language,
        privacy_policy_version: '1.0',
        data_usage_purposes: ['marketing', 'analytics'],
        last_updated_by: 'web_form',
        lancamento: LANCAMENTO,
        pesquisa_hash: pesquisaHash
      };

      const { error: insertError } = await supabase
        .from('leads_lancamento')
        .insert([leadData]);

      if (insertError) {
        // Se o erro for de unique_violation, significa que o lead já existe (race condition)
        if (insertError.code === '23505') {
          const encryptedData = encryptData({ email: emailTrimmed, lancamento: LANCAMENTO });
          navigate(`/inscricao/obrigado?data=${encryptedData}`);
          return;
        }
        throw insertError;
      }

      const encryptedData = encryptData({ email: emailTrimmed, lancamento: LANCAMENTO });
      navigate(`/inscricao/obrigado?data=${encryptedData}`);
    } catch (error) {
      console.error('Error saving lead:', error);
      setError('Ocorreu um erro ao realizar sua inscrição. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <section className="bg-gradient-to-r from-[#0d0d0d] to-[#1a1a1a] text-white py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex justify-center md:hidden mb-8">
            <GradientLogo size="large" />
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="w-full md:w-1/2 space-y-8 animate-fadeIn order-2 md:order-1">
              <div className="flex justify-center mb-8">
                <GradientLogo size="large" />
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-center">
                Do Zero ao Dev Disputado(a)
              </h1>
              
              <p className="text-xl md:text-2xl opacity-90 font-light text-center">
                Um evento 100% gratuito para quem quer virar o jogo na carreira de programação.
              </p>
              
              <div className="flex items-center gap-4 flex-wrap justify-center">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#f2055c]" />
                  <span className="text-sm">Data do evento:</span>
                </div>
                
                <div className="flex items-center gap-2 bg-[#f2055c]/10 px-4 py-2 rounded-lg">
                  <span className="font-semibold">{eventDate.date}</span>
                  <span className="text-[#f2055c]">•</span>
                  <span className="font-semibold">{eventDate.time}</span>
                </div>
              </div>
            </div>
            
            <div className="w-full md:w-5/12 bg-white/10 backdrop-blur p-6 rounded-lg shadow-xl border border-white/20 animate-fadeInUp order-1 md:order-2">
              <h2 className="text-2xl font-bold mb-4 text-center">Inscreva-se Gratuitamente</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="text-sm font-medium block mb-1">
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded bg-white/5 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#f2055c]"
                    placeholder="Seu nome completo"
                    disabled={isLoading}
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="text-sm font-medium block mb-1">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded bg-white/5 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#f2055c]"
                    placeholder="seu.email@exemplo.com"
                    disabled={isLoading}
                  />
                </div>

                <div className="text-center text-sm text-gray-300 bg-white/5 py-2 px-3 rounded">
                  <Calendar className="w-4 h-4 inline-block mr-1 text-[#f2055c]" />
                  {eventDate.date} às {eventDate.time}
                </div>

                <div className="text-sm text-gray-300">
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      required
                      className="mt-1"
                    />
                    <span>
                      Concordo com a{' '}
                      <button
                        type="button"
                        onClick={() => setIsPrivacyPolicyOpen(true)}
                        className="text-[#f2055c] hover:underline"
                      >
                        Política de Privacidade
                      </button>{' '}
                      e autorizo o uso dos meus dados para receber comunicações sobre o evento.
                    </span>
                  </label>
                </div>
                
                {error && (
                  <div className="text-red-400 text-sm text-center mt-2">
                    {error}
                  </div>
                )}
                
                <Button type="submit" fullWidth disabled={isLoading}>
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processando...
                    </span>
                  ) : (
                    'Quero me inscrever'
                  )}
                </Button>
                
                <p className="text-xs text-center opacity-70 mt-4">
                  Suas informações estão seguras e nunca serão compartilhadas.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Modal
        isOpen={isPrivacyPolicyOpen}
        onClose={() => setIsPrivacyPolicyOpen(false)}
        title="Política de Privacidade"
      >
        <PrivacyPolicyContent />
      </Modal>
    </>
  );
};

export default Hero;